import { InternalOrganization, InternalUser } from '../../organizations/types/Organizations'
import { GAMEREFINERY_ORGANIZATION_ID } from '../constants'
import {
  AnalysisSuggestion,
  AnalysisSuggestionChange,
  AnalysisSuggestionGame,
  AnalysisSuggestionRow,
  AnalysisSuggestionsSummaryData,
  AnalysisSuggestionSort,
  AnalysisSuggestionType,
  AnalystOption,
} from '../types/AnalysisSuggestion'

export const ANALYSIS_SUGGESTIONS_PER_PAGE = 50

const fullName = (user?: Pick<InternalUser, 'firstName' | 'lastName'>) => [user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined

/** Legacy's rule: a game without GPS values has never been analysed. A game `games/list` doesn't return has no type. */
export const getAnalysisSuggestionType = (game?: AnalysisSuggestionGame) => {
  if (!game) return undefined
  return game.gpsPerMarket && Object.keys(game.gpsPerMarket).length > 0 ? AnalysisSuggestionType.Update : AnalysisSuggestionType.New
}

/** The GameRefinery organization's users with a first name, as legacy lists them. */
export const getAnalystOptions = (users: InternalUser[] = []): AnalystOption[] =>
  users
    .filter((user) => user.organization === GAMEREFINERY_ORGANIZATION_ID && user.firstName)
    .sort((a, b) => (a.firstName ?? '').localeCompare(b.firstName ?? ''))
    .map((user) => ({ email: user.email, name: fullName(user) ?? user.email }))

export const uniqueValues = <T>(values: T[]) => Array.from(new Set(values))

export const toAnalysisSuggestionRows = (
  suggestions: AnalysisSuggestion[],
  {
    games = [],
    organizations = [],
    users = [],
    analysts = [],
  }: { games?: AnalysisSuggestionGame[]; organizations?: InternalOrganization[]; users?: InternalUser[]; analysts?: AnalystOption[] }
): AnalysisSuggestionRow[] => {
  const gamesById = new Map(games.map((game) => [game.id, game]))
  const organizationsById = new Map(organizations.map((organization) => [organization.id, organization]))
  const usersByEmail = new Map(users.map((user) => [user.email, user]))
  const analystsByEmail = new Map(analysts.map((analyst) => [analyst.email, analyst]))
  return suggestions.map((suggestion) => {
    const game = gamesById.get(suggestion.gameId)
    return {
      suggestion,
      game,
      type: getAnalysisSuggestionType(game),
      organizationName: organizationsById.get(suggestion.requestingOrganization)?.name,
      userName: fullName(usersByEmail.get(suggestion.requestingUser)),
      analystName: suggestion.assignedAnalyst ? analystsByEmail.get(suggestion.assignedAnalyst)?.name : undefined,
    }
  })
}

const countBy = <Key>(rows: AnalysisSuggestionRow[], key: (row: AnalysisSuggestionRow) => Key) => {
  const counts = new Map<Key, { row: AnalysisSuggestionRow; count: number }>()
  rows.forEach((row) => {
    const entry = counts.get(key(row))
    if (entry) entry.count += 1
    else counts.set(key(row), { row, count: 1 })
  })
  return Array.from(counts.entries())
}

const mostCounted = <Key>(entries: [Key, { row: AnalysisSuggestionRow; count: number }][]) =>
  entries.reduce<[Key, { row: AnalysisSuggestionRow; count: number }] | undefined>(
    (top, entry) => (!top || entry[1].count > top[1].count ? entry : top),
    undefined
  )

export const roundToTwo = (value: number) => Math.round(value * 100) / 100

export const getAnalysisSuggestionsSummary = (rows: AnalysisSuggestionRow[]): AnalysisSuggestionsSummaryData => {
  const byOrganization = countBy(rows, (row) => row.suggestion.requestingOrganization)
  const byUser = countBy(rows, (row) => row.suggestion.requestingUser)
  const topOrganization = mostCounted(byOrganization)
  const topUser = mostCounted(byUser)
  return {
    total: rows.length,
    organizationCount: byOrganization.length,
    userCount: byUser.length,
    topOrganization: topOrganization && { id: topOrganization[0], name: topOrganization[1].row.organizationName, count: topOrganization[1].count },
    topUser: topUser && { email: topUser[0], name: topUser[1].row.userName, count: topUser[1].count },
    averageImportance: rows.length ? roundToTwo(rows.reduce((sum, { suggestion }) => sum + suggestion.importance, 0) / rows.length) : 0,
    byOrganization: byOrganization
      .filter(([, { row }]) => row.organizationName)
      .map(([, { row, count }]) => ({ name: row.organizationName as string, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
  }
}

const searchableText = ({ suggestion, game, organizationName, userName, analystName }: AnalysisSuggestionRow) =>
  [
    suggestion.id,
    game?.name,
    game?.artist,
    suggestion.gameVersion,
    suggestion.requestReason,
    suggestion.internalComment,
    suggestion.requestingUser,
    userName,
    organizationName,
    suggestion.assignedAnalyst,
    analystName,
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase()

export type AnalysisSuggestionFilters = { text: string; showNew: boolean; showUpdate: boolean }

/** The text and type filters. Suggestions without a type show only when both types do, as in legacy. */
export const filterAnalysisSuggestionRows = (rows: AnalysisSuggestionRow[], { text, showNew, showUpdate }: AnalysisSuggestionFilters) => {
  const term = text.trim().toLowerCase()
  return rows.filter((row) => {
    if (!(showNew && showUpdate)) {
      if (row.type === AnalysisSuggestionType.New ? !showNew : row.type === AnalysisSuggestionType.Update ? !showUpdate : true) return false
    }
    return !term || searchableText(row).includes(term)
  })
}

const compareText = (a?: string | null, b?: string | null) => (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' })

const comparators: { [sort in AnalysisSuggestionSort]: (a: AnalysisSuggestionRow, b: AnalysisSuggestionRow) => number } = {
  [AnalysisSuggestionSort.Date]: () => 0,
  [AnalysisSuggestionSort.Organization]: (a, b) =>
    compareText(a.organizationName, b.organizationName) || compareText(a.suggestion.requestingOrganization, b.suggestion.requestingOrganization),
  [AnalysisSuggestionSort.User]: (a, b) => compareText(a.suggestion.requestingUser, b.suggestion.requestingUser),
  [AnalysisSuggestionSort.Importance]: (a, b) => a.suggestion.importance - b.suggestion.importance,
  [AnalysisSuggestionSort.Game]: (a, b) => compareText(a.game?.name, b.game?.name),
}

/** Sorted by the chosen field, then by date, both in the same direction. */
export const sortAnalysisSuggestionRows = (rows: AnalysisSuggestionRow[], sort: AnalysisSuggestionSort, descending: boolean) => {
  const direction = descending ? -1 : 1
  return [...rows].sort((a, b) => direction * (comparators[sort](a, b) || a.suggestion.createdAt - b.suggestion.createdAt))
}

/** Legacy's body: the stored suggestion with the change, in the stored key order. */
export const toAnalysisSuggestionWrite = (stored: AnalysisSuggestion, change: AnalysisSuggestionChange): AnalysisSuggestion => ({ ...stored, ...change })

export const isChangeStored = (stored: AnalysisSuggestion | undefined, change: AnalysisSuggestionChange) =>
  !!stored && (Object.keys(change) as (keyof AnalysisSuggestionChange)[]).every((field) => stored[field] === change[field])

/** An editor that was emptied gives `null`, the stored value of suggestions without a comment. */
export const toStoredComment = (html: string) => (html.trim() ? html : null)
